import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calender, Search, Transfer } from "../shared/icon";

import axiosInstance from "../api/axiosInstance";
import { isAuthenticated } from "../utils/auth";

const getRangeForDays = (days) => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { from: from.toISOString(), to: to.toISOString() };
};

const dateToISOStart = (yyyyMmDd) =>
  yyyyMmDd ? new Date(`${yyyyMmDd}T00:00:00.000Z`).toISOString() : null;
const dateToISOEnd = (yyyyMmDd) =>
  yyyyMmDd ? new Date(`${yyyyMmDd}T23:59:59.999Z`).toISOString() : null;

const ReportTab = () => {
  const [activeTab, setActiveTab] = useState("commission");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [activeFilter, setActiveFilter] = useState("7");
  const [showCalendar, setShowCalendar] = useState(false);
  const [pickerFrom, setPickerFrom] = useState("");
  const [pickerTo, setPickerTo] = useState("");
  const calendarRef = useRef(null);

  const isLoggedIn = useMemo(
    () => (typeof window !== "undefined" ? isAuthenticated() : false),
    [],
  );

  const displayDate = (d) =>
    d
      ? new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" })
      : "—";

  const fetchCommissionReport = async (from, to) => {
    if (!from || !to || !isLoggedIn) return;
    try {
      setLoading(true);
      const payload = { from_date: from, to_date: to };
      const res = await axiosInstance.post("/report/getMyAffComm", payload);
      if (res?.data?.status) {
        const data = res.data?.data;
        setRows(Array.isArray(data) ? data : (data?.response ?? []));
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserReport = async (from, to) => {
    if (!from || !to || !isLoggedIn) return;
    try {
      setLoading(true);
      const payload = { from_date: from, to_date: to };
      const res = await axiosInstance.post(
        "/report/getAffUserCreatedCountDateWise",
        payload,
      );
      if (res?.data?.status && Array.isArray(res.data?.data)) {
        setRows(res.data.data);
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;

    const { from, to } = getRangeForDays(7);
    setFromDate(from);
    setToDate(to);
    setActiveFilter("7");

    if (activeTab === "commission") {
      fetchCommissionReport(from, to);
    } else {
      fetchUserReport(from, to);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLoggedIn]);

  const handleQuickFilter = (days) => {
    if (!isLoggedIn) return;
    setActiveFilter(String(days));
    const { from, to } = getRangeForDays(days);
    setFromDate(from);
    setToDate(to);
    if (activeTab === "commission") {
      fetchCommissionReport(from, to);
    } else {
      fetchUserReport(from, to);
    }
  };

  const openCalendar = () => {
    if (!isLoggedIn) return;
    setPickerFrom(fromDate ? fromDate.slice(0, 10) : "");
    setPickerTo(toDate ? toDate.slice(0, 10) : "");
    setShowCalendar(true);
  };

  const applyCalendarRange = () => {
    if (!isLoggedIn) return;
    const from = dateToISOStart(pickerFrom);
    const to = dateToISOEnd(pickerTo);
    if (from && to) {
      setFromDate(from);
      setToDate(to);
      setActiveFilter(null);
      setShowCalendar(false);
      if (activeTab === "commission") {
        fetchCommissionReport(from, to);
      } else {
        fetchUserReport(from, to);
      }
    }
  };

  const handleSearch = () => {
    if (!isLoggedIn) return;
    if (!fromDate || !toDate) return;
    if (activeTab === "commission") {
      fetchCommissionReport(fromDate, toDate);
    } else {
      fetchUserReport(fromDate, toDate);
    }
  };

  useEffect(() => {
    if (!showCalendar) return;
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar]);

  return (
    <>
      <div className="report-toggle">
        <div
          className={`report-tab ${
            activeTab === "commission" ? "active-tab" : ""
          }`}
          onClick={() => {
            setActiveTab("commission");
          }}
        >
          <span>Commission Report</span>
        </div>

        <div
          className={`report-tab ${activeTab === "user" ? "active-tab" : ""}`}
          onClick={() => setActiveTab("user")}
        >
          <span>User Report</span>
        </div>
      </div>

      <div className="filter-card">
        <div className="filter-inner">
          <div className="date-search-row">
            <div
              className="date-range-box"
              ref={calendarRef}
              style={{ position: "relative" }}
            >
              <div className="date-text">
                <div className="date-values">
                  <span>
                    {isLoggedIn && fromDate
                      ? displayDate(fromDate)
                      : "December 30, 2025"}
                  </span>
                  <Transfer />
                  <span>
                    {isLoggedIn && toDate
                      ? displayDate(toDate)
                      : "January 6, 2026"}
                  </span>
                </div>
              </div>

              <Calender
                onClick={openCalendar}
                style={{ cursor: isLoggedIn ? "pointer" : "default" }}
                role="button"
                aria-label="Select date range"
              />
              {showCalendar && (
                <div
                  className="calendar-popover"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    marginTop: "4px",
                    padding: "12px",
                    background: "var(--color-secondary, #1a1a1a)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "8px",
                    zIndex: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    minWidth: "260px",
                  }}
                >
                  <label style={{ fontSize: "12px", color: "#fff" }}>
                    From
                    <input
                      type="date"
                      value={pickerFrom}
                      onChange={(e) => setPickerFrom(e.target.value)}
                      style={{
                        display: "block",
                        marginTop: "4px",
                        padding: "6px 8px",
                        width: "100%",
                        background: "#333",
                        border: "1px solid #555",
                        borderRadius: "4px",
                        color: "#fff",
                      }}
                    />
                  </label>
                  <label style={{ fontSize: "12px", color: "#fff" }}>
                    To
                    <input
                      type="date"
                      value={pickerTo}
                      onChange={(e) => setPickerTo(e.target.value)}
                      style={{
                        display: "block",
                        marginTop: "4px",
                        padding: "6px 8px",
                        width: "100%",
                        background: "#333",
                        border: "1px solid #555",
                        borderRadius: "4px",
                        color: "#fff",
                      }}
                    />
                  </label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      onClick={applyCalendarRange}
                      className="thm-but btn-gradient"
                      style={{ flex: 1 }}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCalendar(false)}
                      style={{
                        padding: "6px 12px",
                        background: "#444",
                        border: "none",
                        borderRadius: "4px",
                        color: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              className="icon-btn"
              type="button"
              onClick={handleSearch}
              aria-label="Search"
              disabled={!isLoggedIn}
            >
              <Search />
            </button>
            {activeTab === "user" && (
              <form className="filter-form">
                <select id="countries" className="filter-select">
                  <option value="user">User Added</option>
                  <option value="deposit">Deposit / Withdraw</option>
                </select>
              </form>
            )}
          </div>

          <div className="quick-filters">
            <div
              className={`quick-filter ${
                activeFilter === "7" ? "active-filter" : ""
              }`}
              onClick={() => handleQuickFilter(7)}
              onKeyDown={(e) => e.key === "Enter" && handleQuickFilter(7)}
              role="button"
              tabIndex={0}
            >
              Last 7 days
            </div>
            <div
              className={`quick-filter ${
                activeFilter === "14" ? "active-filter" : ""
              }`}
              onClick={() => handleQuickFilter(14)}
              onKeyDown={(e) => e.key === "Enter" && handleQuickFilter(14)}
              role="button"
              tabIndex={0}
            >
              Last 14 days
            </div>
            <div
              className={`quick-filter ${
                activeFilter === "28" ? "active-filter" : ""
              }`}
              onClick={() => handleQuickFilter(28)}
              onKeyDown={(e) => e.key === "Enter" && handleQuickFilter(28)}
              role="button"
              tabIndex={0}
            >
              Last 28 days
            </div>
          </div>
        </div>
      </div>
      <div className="af-table-wrapper">
        <table className="af-table default-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>{activeTab === "commission" ? "Commision" : "Count"}</th>
              {activeTab === "user" && <th>Action</th>}
            </tr>
          </thead>

          <tbody id="userTableBody">
            {loading ? (
              <tr>
                <td
                  colSpan={activeTab === "user" ? 3 : 2}
                  className="no-records"
                >
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={activeTab === "user" ? 3 : 2}
                  className="no-records"
                >
                  No Records Found
                </td>
              </tr>
            ) : activeTab === "commission" ? (
              rows.map((row, index) => (
                <tr key={row._id || row.date || index}>
                  <td>{displayDate(row.date || row.createdAt)}</td>
                  <td>
                    {row.commission ?? row.commision ?? row.amount ?? "—"}
                  </td>
                </tr>
              ))
            ) : (
              rows.map((row, index) => (
                <tr key={row.date || row._id || index}>
                  <td>{displayDate(row.date)}</td>
                  <td>{row.count ?? 0}</td>
                  <td>
                    <button
                      type="button"
                      className="thm-but btn-gradient"
                      style={{
                        padding: "4px 10px",
                        borderRadius: "50px",
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ReportTab;
